// src/review/canvas/navigation/AddRiskModal.jsx
import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Modal, Stack, TextInput, Select, Button, Group, Text } from '@mantine/core';
import { IconPlus, IconX } from '@tabler/icons-react';

/**
 * AddRiskModal - Modal for adding manual risks during review
 * Uses categories from the assessment's template (not hardcoded)
 * Attributes risk to original assessment owner (not reviewer)
 */
function AddRiskModal({
  opened,
  onClose,
  areaKey,
  areaLabel,
  availableCategories,
  onSubmit
}) {
  const [riskName, setRiskName] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Transform categories array to Select options
  const categoryOptions = useMemo(() => {
    if (!availableCategories || availableCategories.length === 0) {
      return [];
    }

    return availableCategories.map(cat => ({
      value: cat.value || cat.name || cat,
      label: cat.type === 'cause'
        ? `${cat.label} (Cause - ${cat.parentSource})`
        : cat.label || cat.name || cat
    }));
  }, [availableCategories]);

  const handleSubmit = async () => {
    // Validation
    if (!riskName.trim()) {
      return;
    }

    if (!selectedCategory) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit({
        areaKey,
        category: selectedCategory,
        riskName: riskName.trim()
      });

      // Reset form on success
      setRiskName('');
      setSelectedCategory('');
      onClose();
    } catch (error) {
      console.error('[AddRiskModal] Error submitting risk:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setRiskName('');
    setSelectedCategory('');
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={`Add Risk to ${areaLabel}`}
      size="md"
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Add a manual risk to this assessment. The risk will be attributed to the original
          assessment owner.
        </Text>

        <TextInput
          label="Risk Name"
          placeholder="Enter risk name..."
          value={riskName}
          onChange={(e) => setRiskName(e.target.value)}
          required
          autoFocus
          data-autofocus
        />

        <Select
          label="Risk Source or Cause"
          placeholder="Select a risk source or cause..."
          value={selectedCategory}
          onChange={setSelectedCategory}
          data={categoryOptions}
          required
          searchable
          disabled={categoryOptions.length === 0}
          description={
            categoryOptions.length === 0
              ? 'No risk sources or causes available for this area'
              : 'Select from available risk sources or root causes'
          }
        />

        <Group justify="flex-end" mt="md">
          <Button
            variant="subtle"
            leftSection={<IconX size={16} />}
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={handleSubmit}
            disabled={!riskName.trim() || !selectedCategory || isSubmitting}
            loading={isSubmitting}
          >
            Add Risk
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

AddRiskModal.propTypes = {
  opened: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  areaKey: PropTypes.string.isRequired,
  areaLabel: PropTypes.string.isRequired,
  availableCategories: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        label: PropTypes.string
      })
    ])
  ).isRequired,
  onSubmit: PropTypes.func.isRequired
};

export default AddRiskModal;
